<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center py-2 px-0">
          <span class="primary--text">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
      </div>

      <v-card-text class="pa-4 editor-content">
        <section class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8"><v-text-field v-model="fields.title" label="Name" hide-details/></v-col>
            <v-col cols="12" md="4"
              ><v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details
            /></v-col>
          </v-row>
        </section>

        <SectionSettingsContainerFuild
          :settings="fields.settings"
          :container-page="fields.containerPage"
          :show-container-page="false"
          :show-background-image="false"
        />

        <section class="mt-6">
          <label class="label-input">Heading</label>
          <ckeditor :editor="editor" v-model="fields.heading.title" :config="editorConfig" />
        </section>

        <section class="mt-6">
          <label class="label-input">Main media</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <v-select v-model="fields.media.type" :items="mediaTypeOptions" label="Media type" hide-details />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="fields.media.position"
                  :items="positionOptions"
                  label="Desktop position"
                  hide-details
                />
              </v-col>
            </v-row>

            <template v-if="fields.media.type === 'video'">
              <v-text-field class="mt-4" v-model="fields.media.video" label="Video URL" hide-details />
              <video
                v-if="fields.media.video"
                class="media-preview"
                :src="fields.media.video"
                controls
                muted
                playsinline
              />
            </template>
            <template v-else>
              <p class="field-label mt-4">Image</p>
              <UploadImage
                :image="{ imageSrc: fields.media.image }"
                height="240"
                type-upload="image"
                @callback="handleUploadMediaImage"
                @callbackDelete="handleRemoveMediaImage"
              />
            </template>
          </div>
        </section>

        <section class="mt-6">
          <div class="d-flex justify-space-between align-center mb-3">
            <label class="label-input mb-0">Features</label>
            <v-btn small color="primary" @click="handleAddFeature"
              ><v-icon left size="16">mdi-plus</v-icon>Add item</v-btn
            >
          </div>
          <v-expansion-panels accordion>
            <v-expansion-panel v-for="(item, index) in fields.features" :key="`feature-${index}`">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center">
                  <span>Feature {{ index + 1 }}</span>
                  <v-btn icon @click.stop="handleRemoveFeature(index)"
                    ><v-icon color="#ff6c6c">mdi-delete</v-icon></v-btn
                  >
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </section>

        <section class="mt-6">
          <div class="d-flex justify-space-between align-center mb-3">
            <label class="label-input mb-0">Compare slides</label>
            <v-btn small color="primary" @click="handleAddSlide"
              ><v-icon left size="16">mdi-plus</v-icon>Add slide</v-btn
            >
          </div>
          <v-expansion-panels accordion>
            <v-expansion-panel v-for="(slide, index) in fields.slides" :key="`slide-${index}`">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center">
                  <span>Slide {{ index + 1 }}</span>
                  <v-btn icon @click.stop="handleRemoveSlide(index)"><v-icon color="#ff6c6c">mdi-delete</v-icon></v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="6">
                    <p class="field-label">Before image</p>
                    <UploadImage
                      :image="{ imageSrc: slide.before }"
                      height="200"
                      type-upload="image"
                      @callback="obj => handleUploadSlideImage(obj, index, 'before')"
                      @callbackDelete="handleRemoveSlideImage(index, 'before')"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <p class="field-label">After image</p>
                    <UploadImage
                      :image="{ imageSrc: slide.after }"
                      height="200"
                      type-upload="image"
                      @callback="obj => handleUploadSlideImage(obj, index, 'after')"
                      @callbackDelete="handleRemoveSlideImage(index, 'after')"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </section>

        <section class="mt-6"><ButtonPrimary :button="fields.button" /></section>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions class="py-2 px-0">
          <v-spacer />
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import ButtonPrimary from './common/button-primary.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const defaultFeature = (
  content = '<h2 style="text-align:center;"><span style="color:hsl(4,98%,78%);"><strong>Feature</strong></span></h2> <p style="text-align:center;"><span style="color:hsl(220,9%,46%);"><strong>Description</strong></span></p>',
) => ({ content });
const defaultSlide = () => ({ before: '', after: '' });

export default {
  name: 'ManchestoreCompareSlider',
  components: { UploadImage, ButtonPrimary, SectionSettingsContainerFuild },
  props: { sectionEditor: { type: Object, required: true } },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      isUploadingVideo: false,
      mediaTypeOptions: [
        { text: 'Video', value: 'video' },
        { text: 'Image', value: 'image' },
      ],
      positionOptions: [
        { text: 'Left', value: 'left' },
        { text: 'Right', value: 'right' },
      ],
      fields: {
        title: 'Manchestore compare slider',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FEF0EF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: { enableStyle: false, background: '#FFFFFF' },
        heading: { title: '' },
        media: { type: 'video', position: 'left', video: '', image: '' },
        features: [],
        slides: [],
        button: {
          show: true,
          backgroundColor: '#FF5900',
          color: '#FFFFFF',
          radius: 999,
          text: 'Get 50% Off Now',
          link: '#',
          iconPrefix: { show: false, src: '' },
          iconSuffix: {
            show: true,
            src: '',
          },
          subBtn: { show: false, text: '' },
        },
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(value) {
        if (value?.sectionExtract) this.applyExtract(value.sectionExtract);
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        heading: { ...this.fields.heading, ...(extract.heading || {}) },
        media: { ...this.fields.media, ...(extract.media || {}) },
        features: Array.isArray(extract.features)
          ? extract.features.map(item => ({ ...defaultFeature(), ...item }))
          : [],
        slides: Array.isArray(extract.slides) ? extract.slides.map(item => ({ ...defaultSlide(), ...item })) : [],
        button: {
          ...this.fields.button,
          ...(extract.button || {}),
          iconPrefix: { ...this.fields.button.iconPrefix, ...((extract.button || {}).iconPrefix || {}) },
          iconSuffix: { ...this.fields.button.iconSuffix, ...((extract.button || {}).iconSuffix || {}) },
          subBtn: { ...this.fields.button.subBtn, ...((extract.button || {}).subBtn || {}) },
        },
      };
    },
    generateObjectID() {
      const hex = value => Math.floor(value).toString(16);
      return hex(Date.now() / 1000) + ' '.repeat(16).replace(/./g, () => hex(Math.random() * 16));
    },
    handleUploadMediaImage(obj) {
      this.fields.media.image = obj?.imageSrc || '';
    },
    handleRemoveMediaImage() {
      this.fields.media.image = '';
    },
    handleAddFeature() {
      this.fields.features.push(defaultFeature());
    },
    handleRemoveFeature(index) {
      this.fields.features.splice(index, 1);
    },
    handleAddSlide() {
      this.fields.slides.push(defaultSlide());
    },
    handleRemoveSlide(index) {
      this.fields.slides.splice(index, 1);
    },
    handleUploadSlideImage(obj, index, key) {
      this.fields.slides[index][key] = obj?.imageSrc || '';
    },
    handleRemoveSlideImage(index, key) {
      this.fields.slides[index][key] = '';
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const html = updateHtmlFromData.sectionManchestoreCompareSlider(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', { ...this.sectionEditor.section, title: this.fields.title, html });
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.media-preview {
  display: block;
  width: 100%;
  max-height: 320px;
  margin-top: 16px;
  background: #000;
}
::v-deep .ck-editor__editable_inline {
  min-height: 90px !important;
}
</style>
